import { LoadingData } from '@/models/viewer.model';
import idGenerator from '@/helpers/idGenerator';

Image.prototype.loadImage = function loadImage(
    viewerService: ViewerService,
    url: string,
    id: number,
    defaultView: boolean,
) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const thisImg = this;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';

    xhr.onload = function onload(e) {
        const target = e.target as XMLHttpRequest;

        if (target.status !== 200) viewerService.setLoadingViewError(true);

        const blob = new Blob([this.response]);
        thisImg.src = window.URL.createObjectURL(blob);

        if (defaultView) {
            viewerService.setDefaultViewLoadingFinished();
            viewerService.loadOtherViews();
        }

        viewerService.setLoadedView(id);

        viewerService.loadingData.imagesLoadedCount += 1;
    };

    if (defaultView) {
        xhr.onprogress = function onprogress(e: ProgressEvent) {
            viewerService.setDefaultViewLoadingProgress(Math.ceil((e.loaded / e.total) * 100));
        };
        xhr.onloadstart = function onloadstart() {
            viewerService.setDefaultViewLoadingProgress(0);
        };
    }

    xhr.onerror = function onerror() {
        viewerService.setLoadingViewError(true);
    };
    xhr.send();
};

class ViewerService {
    loadingData: LoadingData = {
        defaultView: {
            started: false,
            finished: false,
            loadingProgress: 0,
        },
        imagesToLoadCount: 0,
        imagesLoadedCount: 0,
        images: [],
    };

    error: boolean;

    sources: Array<string>;

    constructor(sources: Array<string>) {
        this.error = false;
        this.sources = sources;
        this.initImages();
        this.loadDefaultView();
    }

    public setActiveView(id: number) {
        this.loadingData.images.forEach((view) => {
            view.active = view.id === id;
        });
    }

    private initImages() {
        const getId = idGenerator();

        this.sources.forEach((_, index) => {
            const img = new Image();
            this.loadingData.images.push({
                active: index === 0,
                image: img,
                loaded: false,
                id: getId.next().value as number,
                texture: null,
            });
            this.loadingData.imagesToLoadCount += 1;
        });
    }

    private loadDefaultView() {
        const source = this.sources[0];
        const { image, id } = this.loadingData.images[0];

        this.setDefaultViewLoadingStarted();

        // @ts-ignore
        image.loadImage(this, source, id, true);
    }

    public loadOtherViews() {
        for (let i = 1; i < this.sources.length; i += 1) {
            const source = this.sources[i];
            const { image, id } = this.loadingData.images[i];
            // @ts-ignore
            image.loadImage(this, source, id, false);
        }
    }

    private setDefaultViewLoadingStarted() {
        this.loadingData.defaultView.started = true;
    }

    public setViewTexture(id: number, texture: Record<string, unknown>) {
        const matched = this.loadingData.images.find((el) => el.id === id);

        if (matched) {
            matched.texture = texture;
        }
    }

    public setDefaultViewLoadingProgress(val: number) {
        this.loadingData.defaultView.loadingProgress = val;
    }

    public setDefaultViewLoadingFinished() {
        this.loadingData.defaultView.finished = true;
    }

    public setLoadedView(id: number) {
        const matched = this.loadingData.images.find((el) => el.id === id);

        if (matched) matched.loaded = true;
    }

    public setLoadingViewError(state: boolean) {
        this.error = state;
    }

    public getDefaultViewLoadingProgress() {
        return this.loadingData.defaultView.loadingProgress;
    }

    public getActiveViewPhoto() {
        const activeRoom = this.loadingData.images.find((view) => view.active);
        return activeRoom?.image.src;
    }

    public getActiveView() {
        return this.loadingData.images.find((view) => view.active);
    }

    public getViews() {
        return this.loadingData.images;
    }
}

export default ViewerService;
